/**
 * Şifre Değiştir Diyaloğu — Kullanıcı self-service.
 *
 * Kullanım:
 *  <ChangePasswordDialog
 *    open={open} onOpenChange={setOpen}
 *    forced={forcePasswordChange}
 *    onChanged={handleChanged}
 *  />
 *
 * `forced` true iken (ilk giriş) modal kapatılamaz, "Vazgeç" butonu gizlenir.
 */
import { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { KeyRound, Eye, EyeOff, Loader2, Lock } from "lucide-react";

export default function ChangePasswordDialog({ open, onOpenChange, forced = false, onChanged }) {
  const [oldPw, setOldPw] = useState("");
  const [newPw, setNewPw] = useState("");
  const [confirmPw, setConfirmPw] = useState("");
  const [showOld, setShowOld] = useState(false);
  const [showNew, setShowNew] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) {
      setOldPw(""); setNewPw(""); setConfirmPw("");
      setShowOld(false); setShowNew(false);
    }
  }, [open]);

  const submit = async () => {
    if (newPw.length < 6) {
      toast.error("Yeni şifre en az 6 karakter olmalı");
      return;
    }
    if (newPw !== confirmPw) {
      toast.error("Yeni şifreler eşleşmiyor");
      return;
    }
    if (newPw === oldPw) {
      toast.error("Yeni şifre eskiyle aynı olamaz");
      return;
    }
    setSaving(true);
    try {
      await api.post("/auth/change-password", {
        old_password: oldPw, new_password: newPw,
      });
      toast.success("Şifre değiştirildi");
      onChanged?.();
      onOpenChange(false);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally { setSaving(false); }
  };

  return (
    <Dialog
      open={open}
      onOpenChange={(v) => {
        if (forced && !v) return; // zorunlu iken kapatılamaz
        onOpenChange(v);
      }}
    >
      <DialogContent
        className="rounded-sm max-w-md"
        data-testid="change-password-dialog"
        onEscapeKeyDown={(e) => { if (forced) e.preventDefault(); }}
        onPointerDownOutside={(e) => { if (forced) e.preventDefault(); }}
      >
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <KeyRound className="w-5 h-5 text-blue-400" /> Şifre Değiştir
          </DialogTitle>
          <DialogDescription className="text-xs">
            {forced ? (
              <span className="text-amber-400">
                Bu ilk girişiniz — güvenlik için lütfen şifrenizi değiştirin.
              </span>
            ) : (
              "Yeni şifreyi belirlemek için mevcut şifrenizi doğrulayın."
            )}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-3">
          <div>
            <Label className="text-xs flex items-center gap-1">
              <Lock className="w-3 h-3" /> Mevcut Şifre
            </Label>
            <div className="flex items-center gap-1 mt-1">
              <Input
                data-testid="cp-old-password"
                type={showOld ? "text" : "password"}
                value={oldPw}
                onChange={(e) => setOldPw(e.target.value)}
                className="rounded-sm flex-1"
                autoComplete="current-password"
                autoFocus
              />
              <button
                type="button"
                onClick={() => setShowOld((v) => !v)}
                className="p-2 rounded-sm border border-slate-800 hover:bg-slate-900 text-slate-300"
                title={showOld ? "Gizle" : "Göster"}
              >
                {showOld ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
              </button>
            </div>
          </div>

          <div>
            <Label className="text-xs flex items-center gap-1">
              <Lock className="w-3 h-3" /> Yeni Şifre
            </Label>
            <div className="flex items-center gap-1 mt-1">
              <Input
                data-testid="cp-new-password"
                type={showNew ? "text" : "password"}
                value={newPw}
                onChange={(e) => setNewPw(e.target.value)}
                className="rounded-sm flex-1"
                placeholder="En az 6 karakter"
                autoComplete="new-password"
              />
              <button
                type="button"
                onClick={() => setShowNew((v) => !v)}
                className="p-2 rounded-sm border border-slate-800 hover:bg-slate-900 text-slate-300"
                title={showNew ? "Gizle" : "Göster"}
              >
                {showNew ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
              </button>
            </div>
          </div>

          <div>
            <Label className="text-xs flex items-center gap-1">
              <Lock className="w-3 h-3" /> Yeni Şifre (Tekrar)
            </Label>
            <Input
              data-testid="cp-confirm-password"
              type={showNew ? "text" : "password"}
              value={confirmPw}
              onChange={(e) => setConfirmPw(e.target.value)}
              className="rounded-sm mt-1"
              autoComplete="new-password"
            />
            {confirmPw && newPw !== confirmPw && (
              <div className="text-xs text-rose-400 mt-1" data-testid="cp-mismatch">
                Şifreler eşleşmiyor
              </div>
            )}
          </div>
        </div>

        <DialogFooter className="gap-2">
          {!forced && (
            <Button
              variant="ghost"
              onClick={() => onOpenChange(false)}
              disabled={saving}
              className="rounded-sm"
              data-testid="cp-cancel-btn"
            >
              Vazgeç
            </Button>
          )}
          <Button
            data-testid="cp-submit-btn"
            onClick={submit}
            disabled={saving || !oldPw || !newPw || !confirmPw}
            className="rounded-sm bg-blue-600 hover:bg-blue-500 text-white"
          >
            {saving ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <KeyRound className="w-4 h-4 mr-1" />}
            Şifreyi Değiştir
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
